import { useEffect, useState } from "react";
import { Loader2, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Field } from "@/components/common";
import { brl, errMsg, waLink } from "@/lib/api";
import { shopToken } from "@/lib/shop";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", email: "", birth_date: "", password: "", confirm: "" };

function useSubmit(fn) {
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return [busy, run];
}

function LoginForm({ http, onAuth, st, color }) {
  const [f, setF] = useState({ phone: "", password: "" });
  const [busy, run] = useSubmit(async () => onAuth((await http.post("/account/login", f)).data));
  return (
    <div className="space-y-3">
      <Field label="Celular com DDD"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} inputMode="tel" className="bg-white" data-testid="shop-login-phone" /></Field>
      <Field label="Senha"><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="bg-white" data-testid="shop-login-password" /></Field>
      <Button className="h-12 w-full text-white" style={{ background: color }} disabled={busy || !f.phone || !f.password} onClick={run} data-testid="shop-login-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Entrar</Button>
      {st.telefone && <a href={waLink(st.telefone, "Olá! Esqueci a senha do meu cadastro no delivery.")} target="_blank" rel="noreferrer" className="block text-center text-xs text-slate-500 underline">Esqueceu a senha? Fale com a loja</a>}
    </div>
  );
}

function RegisterForm({ http, onAuth, color }) {
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [busy, run] = useSubmit(async () => {
    const { confirm, ...body } = f;
    onAuth((await http.post("/account/register", body)).data);
  });
  const err = f.password && f.password.length < 6 ? "A senha precisa ter pelo menos 6 caracteres" : f.confirm && f.confirm !== f.password ? "As senhas não conferem" : "";
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Nome *" className="col-span-2"><Input value={f.name} onChange={set("name")} className="bg-white" data-testid="shop-reg-name" /></Field>
        <Field label="Celular com DDD *"><Input value={f.phone} onChange={set("phone")} inputMode="tel" className="bg-white" data-testid="shop-reg-phone" /></Field>
        <Field label="Data de nascimento"><Input type="date" value={f.birth_date} onChange={set("birth_date")} className="bg-white" data-testid="shop-reg-birth" /></Field>
        <Field label="E-mail (opcional)" className="col-span-2"><Input type="email" value={f.email} onChange={set("email")} className="bg-white" data-testid="shop-reg-email" /></Field>
        <Field label="Senha *"><Input type="password" value={f.password} onChange={set("password")} className="bg-white" data-testid="shop-reg-password" /></Field>
        <Field label="Confirmar senha *"><Input type="password" value={f.confirm} onChange={set("confirm")} className="bg-white" data-testid="shop-reg-confirm" /></Field>
      </div>
      {err && <p className="text-xs text-red-600">{err}</p>}
      <Button className="h-12 w-full text-white" style={{ background: color }} disabled={busy || !!err || !f.name || !f.phone || !f.password || f.confirm !== f.password} onClick={run} data-testid="shop-reg-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Criar meu cadastro</Button>
      <p className="text-center text-xs text-slate-500">Com o cadastro seus pedidos ficam mais rápidos e você pode resgatar seus pontos de fidelidade.</p>
    </div>
  );
}

function Profile({ http, me, setMe, st, slug, onClose, color }) {
  const [f, setF] = useState({ name: me.name, email: me.email, birth_date: me.birth_date });
  useEffect(() => { setF({ name: me.name, email: me.email, birth_date: me.birth_date }); }, [me]);
  const [busy, run] = useSubmit(async () => { setMe((await http.put("/account/me", f)).data); toast.success("Dados atualizados"); });
  const logout = () => { shopToken.clear(slug); setMe(null); onClose(); toast.success("Você saiu da sua conta"); };
  const l = me.loyalty;
  return (
    <div className="space-y-3" data-testid="shop-profile">
      <p className="text-sm text-slate-500">Celular: <b className="text-slate-800">{me.phone}</b></p>
      {l.enabled && <p className="rounded-lg px-3 py-2 text-sm font-semibold" style={{ background: `${color}14`, color }} data-testid="shop-profile-balance">Saldo de fidelidade: {l.mode === "points" ? `${l.balance} pontos` : brl(l.balance)}</p>}
      <Field label="Nome"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-white" data-testid="shop-profile-name" /></Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="E-mail"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-white" /></Field>
        <Field label="Data de nascimento"><Input type="date" value={f.birth_date} onChange={(e) => setF({ ...f, birth_date: e.target.value })} className="bg-white" /></Field>
      </div>
      <div className="flex gap-2">
        <Button className="flex-1 text-white" style={{ background: color }} disabled={busy || f.name.trim().length < 2} onClick={run} data-testid="shop-profile-save">Salvar dados</Button>
        <Button variant="outline" className="bg-white" onClick={logout} data-testid="shop-logout-btn"><LogOut className="mr-1 h-4 w-4" />Sair</Button>
      </div>
      {!st.loyalty?.enabled && null}
    </div>
  );
}

export function AccountDialog({ open, onOpenChange, http, me, setMe, st, slug, color }) {
  const onAuth = (data) => { shopToken.set(slug, data.token); setMe(data.profile); onOpenChange(false); toast.success(`Olá, ${data.profile.name.split(" ")[0]}!`); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto bg-white text-slate-900" data-testid="shop-account-modal">
        <DialogHeader><DialogTitle className="text-slate-900">{me ? `Olá, ${me.name.split(" ")[0]}` : "Entrar ou cadastrar"}</DialogTitle></DialogHeader>
        {me ? <Profile http={http} me={me} setMe={setMe} st={st} slug={slug} onClose={() => onOpenChange(false)} color={color} /> : (
          <Tabs defaultValue="login">
            <TabsList className="grid w-full grid-cols-2"><TabsTrigger value="login" data-testid="shop-tab-login">Entrar</TabsTrigger><TabsTrigger value="register" data-testid="shop-tab-register">Cadastrar</TabsTrigger></TabsList>
            <TabsContent value="login" className="mt-4"><LoginForm http={http} onAuth={onAuth} st={st} color={color} /></TabsContent>
            <TabsContent value="register" className="mt-4"><RegisterForm http={http} onAuth={onAuth} color={color} /></TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
